<template>
  <Content nav="true" admin="true">
    <h1>{{ title }}</h1>
    <h2>Contact Info</h2>
    <div class="pure-g">
      <div class="pure-u-1-2">
        <h4>Name</h4>
        {{ order.contactInfo.name }}
        <h4>Email</h4>
        {{ order.contactInfo.email }}
        <h4>Phone</h4>
        {{ order.contactInfo.phone }}
      </div>
      <div class="pure-u-1-2">
        <h4>Address</h4>
        <span v-html="contactAddress"></span>
      </div>
    </div>
    <h2>Event Info</h2>
    <div class="pure-g">
      <div class="pure-u-1-2">
        <h4>Date</h4>
        {{ order.eventInfo.date | moment }}
      </div>
      <div class="pure-u-1-2">
        <h4>Address</h4>
        <span v-html="eventAddress"></span>
      </div>
    </div>
    <h2>Order Info</h2>
    <div class="pure-g">
      <div :class="columnCount" v-if="order.orderInfo.sugarCookies">
        <h3>Sugar Cookies</h3>
        <h4>Number of Dozen(s)</h4>
        {{ order.orderInfo.sugarCookiesDetails.numberOfDozens }}
        <h4>Style</h4>
        {{ order.orderInfo.sugarCookiesDetails.style }}
        <h4>Size</h4>
        {{ order.orderInfo.sugarCookiesDetails.cookieSize }}
      </div>
      <div :class="columnCount" v-if="order.orderInfo.macs">
        <h3>Macs</h3>
        <h4>Number of Dozen(s)</h4>
        {{ order.orderInfo.macsDetails.numberOfDozens }}
        <h4>Style</h4>
        {{ order.orderInfo.macsDetails.style }}
        <h4>Notes</h4>
        {{ order.orderInfo.macsDetails.notes }}
      </div>
      <div :class="columnCount" v-if="order.orderInfo.cupcakes">
        <h3>Cupcakes</h3>
        <h4>Number of Dozen(s)</h4>
        {{ order.orderInfo.cupcakesDetails.numberOfDozens }}
        <h4>Style</h4>
        {{ order.orderInfo.cupcakesDetails.style }}
        <h4>Decor</h4>
        {{ order.orderInfo.cupcakesDetails.decor }}
      </div>
      <div :class="columnCount" v-if="order.orderInfo.cakes">
        <h3>Cakes</h3>
        <h4>Number of People</h4>
        {{ order.orderInfo.cakesDetails.numberOfPeople }}
        <h4>Style</h4>
        {{ order.orderInfo.cakesDetails.style }}
        <h4>Requests/Ideas</h4>
        {{ order.orderInfo.cakesDetails.requestsIdeas }}
      </div>
      <div :class="columnCount" v-if="order.orderInfo.other">
        <h3>Other</h3>
        <h4>Notes</h4>
        {{ order.orderInfo.otherDetails.notes }}
      </div>
    </div>
    <h2>Special Requests</h2>
    {{ order.orderInfo.specialRequests }}
    <h2>Notes</h2>
    {{ order.orderInfo.notes }}
    <hr>
    <form class="pure-form pure-form-stacked" @change="save">
      <div class="pure-control-group">
        <textarea v-model="order.orderNotes" placeholder="Order Notes"></textarea>
      </div>
      <div class="pure-control-group">
        <label class="pure-radio" v-for="option in statusOptions" :key="option" :for="option">
          <input :id="option" type="radio" name="status" :value="option" v-model="order.status">
          {{ option | formattedStatus }}
        </label>
      </div>
    </form>
  </Content>
</template>

<script>
import Moment from 'moment'

export default {
  name: 'Order',
  props: ['id'],
  components: { Moment },
  data() {
    return {
      saving: false,
      statusOptions: ['pending', 'hold', 'started', 'complete'],
      order: {
        status: 'pending',
        submittedDate: null,
        orderNotes: '',
        contactInfo: {
          name: null,
          email: null,
          phone: null,
          addressLine1: null,
          addressLine2: null,
          city: null,
          state: null,
          zip: null
        },
        eventInfo: {
          date: null,
          addressLine1: null,
          addressLine2: null,
          city: null,
          state: null,
          zip: null
        },
        orderInfo: {
          sugarCookies: false,
          sugarCookiesDetails: {
            numberOfDozens: null,
            style: 'Basic',
            styleOptions: ['Basic', 'Detailed', 'Elaborate'],
            cookieSize: 'Mini 1-2"',
            cookieSizeOptions: [
              'Mini 1-2"',
              'Small 2-3"',
              'Medium 3-4"',
              'Large 4"+'
            ]
          },
          macs: false,
          macsDetails: {
            numberOfDozens: null,
            style: 'Single Flavor',
            styleOptions: ['Single Flavor', 'Multiflavor'],
            notes: null
          },
          cupcakes: false,
          cupcakesDetails: {
            numberOfDozens: null,
            style:
              'Basic (chocolate/yellow/white cake, single flavor frosting)',
            styleOptions: [
              'Basic (chocolate/yellow/white cake, single flavor frosting)',
              'Signature (lemon and blackberry/Black Forest/custom flavors)'
            ],
            decor: 'Simple/Chill',
            decorOptions: ['Simple/Chill', 'Glam', 'Epic']
          },
          cakes: false,
          cakesDetails: {
            numberOfPeople: null,
            style: 'Basic Celebration',
            styleOptions: ['Basic Celebration', 'Signature', 'Custom'],
            requestsIdeas: null
          },
          other: false,
          otherDetails: {
            notes: null
          },
          specialRequests: null,
          notes: null
        }
      }
    }
  },
  filters: {
    moment: function(date) {
      return Moment(date).format('MMMM D, YYYY @ h:mma')
    },
    formattedStatus: function(status) {
      return status.toUpperCase()
    }
  },
  computed: {
    ref() {
      return this.$database.collection('orders').doc(this.id)
    },
    title() {
      if (this.saving) {
        return 'Saving...'
      } else {
        return `Order: ${this.id}`
      }
    },
    contactAddress() {
      return this.formatAddress(
        this.order.contactInfo.addressLine1,
        this.order.contactInfo.addressLine2,
        this.order.contactInfo.city,
        this.order.contactInfo.state,
        this.order.contactInfo.zip
      )
    },
    eventAddress() {
      return this.formatAddress(
        this.order.eventInfo.addressLine1,
        this.order.eventInfo.addressLine2,
        this.order.eventInfo.city,
        this.order.eventInfo.state,
        this.order.eventInfo.zip
      )
    },
    columnCount() {
      var count = 0
      if (this.order.orderInfo.sugarCookies) {
        count = count + 1
      }
      if (this.order.orderInfo.macs) {
        count = count + 1
      }
      if (this.order.orderInfo.cupcakes) {
        count = count + 1
      }
      if (this.order.orderInfo.cakes) {
        count = count + 1
      }
      if (this.order.orderInfo.other) {
        count = count + 1
      }
      if (count === 0) {
        count = 1
      }
      return `pure-u-1-${count}`
    }
  },
  methods: {
    exists(val) {
      return val !== '' && val !== null
    },
    formatAddress(line1, line2, city, state, zip) {
      var address = ''
      if (this.exists(line1)) {
        address = address + line1 + '<br>'
      }
      if (this.exists(line2)) {
        address = address + line2 + '<br>'
      }
      if (this.exists(city)) {
        address = address + city
      }
      if (this.exists(city) && this.exists(state)) {
        address = address + ', '
      }
      if (this.exists(state)) {
        address = address + state
      }
      if (this.exists(zip)) {
        address = address + ' ' + zip
      }
      return address
    },
    get() {
      this.ref.get().then(doc => (this.order = doc.data()))
    },
    save() {
      this.saving = true
      this.ref.set(this.order).then(() => (this.saving = false))
    }
  },
  mounted() {
    this.get()
  }
}
</script>

<style lang="less" scoped>
h2 {
  margin-top: 1.5em;
  margin-bottom: 0;
}

h4 {
  margin-bottom: 0;
  margin-top: 1em;
}

hr {
  border: none;
  margin-top: 3em;
}

label {
  font-weight: bold;
}

.pure-radio {
  display: inline-block;
  margin-right: 3em;
}

textarea {
  height: 10em;
  width: 100%;
}
</style>
